import { Grid } from "@mui/material";
import { EdmButton } from "atoms";
import { SimpleBoxForm } from "atoms/Forms";
import { addDays, addHours, format, parseISO, setHours, setMinutes, setSeconds, subDays } from "date-fns";
import EdmDialog from "molecules/EdmDialog";
import DefaultToolbar from "organisms/DefaultToolbar";
import { EventRecordType, NonStaffEventRecordType } from "pages/Event/Event.types";
import { DateTimeInput, useTranslate } from "react-admin";
import { useFormContext } from "react-hook-form";

type ScheduledEmailDialogPropsType = {
  isOpen: boolean;
  closeDialog: () => void;
  event?: EventRecordType | NonStaffEventRecordType;
  setScheduledAt: (scheduled_at: string) => void;
  emailFormIsValid: boolean;
};

function ScheduledEmailDialogFormContent({ event }: { event?: EventRecordType | NonStaffEventRecordType }) {
  const { setValue } = useFormContext();
  const translate = useTranslate();

  const updateInputValue = (parsedDate: Date) => {
    const adjustedDate = setHours(setMinutes(setSeconds(parsedDate, 0), 0), 8);

    const formattedDateTimeString = format(adjustedDate, "yyyy-MM-dd'T'HH:mm");
    setValue("scheduled_at", formattedDateTimeString);
  };

  const setScheduledAtInput = (when: string) => {
    switch (when) {
      case "day_before":
        if (event?.first_day) {
          updateInputValue(subDays(parseISO(event.first_day), 1));
        }
        break;

      case "day_after":
        if (event && event.dates.length > 0) {
          const lastDate = event.dates[event.dates.length - 1];
          updateInputValue(addDays(parseISO(lastDate.day), 1));
        }
        break;

      default:
        break;
    }
  };

  return (
    <Grid container spacing={4}>
      {event && (
        <Grid item xs={12}>
          <EdmButton
            label={translate("emailing.day_before_event_start")}
            onClick={() => setScheduledAtInput("day_before")}
          />
          <EdmButton
            label={translate("emailing.day_after_event_end")}
            onClick={() => setScheduledAtInput("day_after")}
            sx={{ ml: 1 }}
          />
        </Grid>
      )}
      <Grid item xs={12} lg={6}>
        <DateTimeInput
          defaultValue={addHours(new Date(), 1)}
          source="scheduled_at"
          label="resources.emails.fields.scheduled_at"
          fullWidth
          required
        />
      </Grid>
    </Grid>
  );
}

export default function ScheduledEmailDialog(props: ScheduledEmailDialogPropsType) {
  const { isOpen, closeDialog, setScheduledAt, event, emailFormIsValid } = props;
  const translate = useTranslate();

  const handleSubmit = (formData: { scheduled_at?: Date }) => {
    if (formData.scheduled_at) {
      setScheduledAt(format(formData.scheduled_at, "yyyy-MM-dd'T'HH:mm:ss.SSSX"));
    }
    closeDialog();
  };

  return (
    <EdmDialog open={isOpen} onClose={closeDialog} title={translate("emailing.scheduled_dispatch")}>
      {() => (
        <SimpleBoxForm
          onSubmit={handleSubmit}
          toolbar={
            <DefaultToolbar
              saveButtonLabel="emailing.scheduled_send"
              disabled={!emailFormIsValid}
              alwaysEnableSaveButton={emailFormIsValid}
            />
          }
          sx={{ padding: 0 }}
        >
          <ScheduledEmailDialogFormContent event={event} />
        </SimpleBoxForm>
      )}
    </EdmDialog>
  );
}
